import {
  THEME_KEYS,
  SIDEBAR_KEYS,
  entityEditorSidebarKey,
  rolodexSidebarKey,
} from "@config/shared/storage-keys";

// Pre-normalisation key -> current key. Runs once per page load (main.jsx)
// so returning users keep their theme/accessibility prefs and sidebar widths.
const STATIC_RENAMES = Object.freeze({
  themeType: THEME_KEYS.TYPE,
  colorPreset: THEME_KEYS.COLOR_PRESET,
  contrast: THEME_KEYS.CONTRAST,
  responsiveFontSizes: THEME_KEYS.RESPONSIVE_FONT_SIZES,
  "expandableSidebar.width": SIDEBAR_KEYS.DEFAULT,
  "assetsSidebar.width": SIDEBAR_KEYS.ASSETS,
  "helpSidebar.width": SIDEBAR_KEYS.HELP,
  "helpTopicsSidebar.width": SIDEBAR_KEYS.HELP_TOPICS,
});

// Every rolodex page shared one width under a key named after the first page
// built on Rolodex2. Each page now has its own key, so the shared value seeds
// all pages that existed at the time — a list frozen on purpose: pages added
// later never had a legacy value to inherit.
const LEGACY_SHARED_ROLODEX_KEY = "knowledgeTemplatesSidebar.width";
const LEGACY_ROLODEX_PAGE_TYPES = Object.freeze([
  "users",
  "groups",
  "contacts",
  "documents",
  "images",
  "videos",
  "audios",
  "decisiontrees",
  "schematics",
  "feedbacks",
  "notifications",
  "news",
  "fragments",
  "assettemplates",
]);

const LEGACY_ENTITY_EDITOR_RE = /^entityEditorSidebar\.(.+)\.width$/;

// Legacy entity-editor keys embedded the raw store value: the lowercase store
// entityType on entity pages, the upper-snake category API key on taxonomy pages.
const legacyEntityEditorTarget = (segment) => {
  if (segment === "default") return entityEditorSidebarKey();
  const isCategoryApiKey = segment === segment.toUpperCase();
  return isCategoryApiKey
    ? entityEditorSidebarKey({ category: segment })
    : entityEditorSidebarKey({ entityType: segment });
};

// Current key(s) a legacy key maps to; empty when the key is not ours.
const targetsFor = (legacyKey) => {
  if (Object.hasOwn(STATIC_RENAMES, legacyKey)) return [STATIC_RENAMES[legacyKey]];
  if (legacyKey === LEGACY_SHARED_ROLODEX_KEY) {
    return LEGACY_ROLODEX_PAGE_TYPES.map((type) => rolodexSidebarKey(type));
  }
  const match = LEGACY_ENTITY_EDITOR_RE.exec(legacyKey);
  return match ? [legacyEntityEditorTarget(match[1])] : [];
};

/**
 * Renames legacy localStorage keys in place. A value already stored under the
 * new key wins; the legacy key is removed either way. Returns the legacy keys
 * it touched. Never throws — storage may be unavailable (privacy mode,
 * sandboxed iframe).
 */
export const migrateLegacyStorageKeys = (storage = globalThis.localStorage) => {
  const migrated = [];
  try {
    const legacyKeys = [];
    for (let index = 0; index < storage.length; index += 1) {
      legacyKeys.push(storage.key(index));
    }
    for (const legacyKey of legacyKeys) {
      const targets = legacyKey ? targetsFor(legacyKey) : [];
      if (targets.length === 0) continue;
      const value = storage.getItem(legacyKey);
      for (const target of targets) {
        if (storage.getItem(target) === null) storage.setItem(target, value);
      }
      storage.removeItem(legacyKey);
      migrated.push(legacyKey);
    }
  } catch {
    /* storage unavailable */
  }
  return migrated;
};
